import { Flex, useToast } from "@chakra-ui/react";
import { useHasAccessTo } from "@intentsify/authorization/dist/react";
import { ListCampaignsRO } from "@intentsify/dto";
import {
  CampaignForList,
  RoomEventType,
  SaasRoleName,
  SocketRoom,
  SortDirection,
} from "@intentsify/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { RowSelectionState } from "@tanstack/react-table";
import {
  CampaignsTableFiltersState,
  ListCampaignsPageParams,
  defaultListCampaignsPageParams,
  listCampaignsPage,
} from "api";
import {
  Button,
  Search,
  Table as TableComponent,
  TableColumnsToggle,
  useRowSelection,
} from "components";
import { PaginationType } from "components/Table/Table/Table.types";
import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { CampaignActionsItems } from "shared/components";
import { useUser } from "store/store.hooks";
import { FetchDataParams, HttpStatus } from "types";
import { handleApiError, shouldRetryUnlessStatus } from "utils";
import { useRoomEvent } from "webSocket";
import { CreateCampaignScreenDefinition } from "../../CampaignsWizard";
import {
  FavoriteStateChange,
  updateCampaignFavoriteStatus,
} from "../YourCampaigns.requests";
import { useCampaignsTableColumns } from "./campaignsTable.columns";
import { CampaignsTableFilters } from "./CampaignsTableFilters";
import { BulkActions, Stats } from "./components";

const CampaignsTable = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUser();
  const toast = useToast();

  const storeParamsInQuery = useCallback(
    (params: ListCampaignsPageParams) => {
      const query = btoa(JSON.stringify(params));

      navigate(location.pathname + `?${query}`, { replace: true });
    },
    [navigate, location.pathname]
  );

  const parseUrl = (): Partial<ListCampaignsPageParams> | null => {
    if (!location.search || location.search === "?fresh") {
      return null;
    }

    try {
      return JSON.parse(
        atob(location.search.slice(1))
      ) as Partial<ListCampaignsPageParams>;
    } catch {
      // Pre-release URLs encoded token pagination state; start fresh.
      return null;
    }
  };

  const getInitialParams = (): {
    search: string | undefined;
    fetchDataParams: ListCampaignsPageParams;
  } => {
    const urlParams = parseUrl();

    return {
      search: urlParams?.search,
      fetchDataParams: {
        ...defaultListCampaignsPageParams,
        ...urlParams,
      },
    };
  };

  const initialParams = getInitialParams();

  useEffect(() => {
    if (location.search === "?fresh") {
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchValue, setSearchValue] = useState<string | undefined>(
    initialParams.search
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const hasAccessToDraftCampaigns = useHasAccessTo("campaign.edit");

  const [fetchDataParams, setFetchDataParams] =
    useState<ListCampaignsPageParams>(initialParams.fetchDataParams);
  const [filters, setFilters] = useState<CampaignsTableFiltersState>({
    isFavorite: initialParams.fetchDataParams.isFavorite,
    onlyDrafts: initialParams.fetchDataParams.onlyDrafts ?? false,
    createdByIds: initialParams.fetchDataParams.createdByIds ?? [],
    companyIds: initialParams.fetchDataParams.companyIds ?? [],
    visibility: initialParams.fetchDataParams.visibility,
    createdFrom: initialParams.fetchDataParams.createdFrom,
    createdTo: initialParams.fetchDataParams.createdTo,
  });

  const campaignsQueryKey = [
    "campaignsList",
    fetchDataParams,
    user,
    hasAccessToDraftCampaigns,
  ];

  const { data, isFetching } = useQuery({
    queryKey: campaignsQueryKey,
    queryFn: () => {
      return listCampaignsPage({
        ...fetchDataParams,
        includeCampaignsInDraft: hasAccessToDraftCampaigns,
      });
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  useRoomEvent(
    SocketRoom.MY_INTENT_MODELS,
    RoomEventType.LIST_CAMPAIGNS_STATE_UPDATE,
    (event) => {
      if (
        data?.campaigns?.find((i) => i.campaignId === event.data.campaignId)
      ) {
        const previousCampaignState =
          queryClient.getQueryData<ListCampaignsRO>(campaignsQueryKey);

        if (previousCampaignState) {
          queryClient.setQueryData(campaignsQueryKey, {
            ...previousCampaignState,
            campaigns: previousCampaignState.campaigns.map((c) => {
              if (c.campaignId === event.data.campaignId) {
                return {
                  ...c,
                  state: event.data.campaignState,
                };
              }
              return c;
            }),
          });
        }
      }
    }
  );

  const handleOnFetchDataChange = useCallback(
    (params: FetchDataParams<keyof CampaignForList>) => {
      setFetchDataParams((prev) => {
        const next: ListCampaignsPageParams = {
          ...params,
          ...filters,
        };

        if (isEqual(prev, next)) {
          return prev;
        }

        storeParamsInQuery(next);
        return next;
      });
    },
    [filters, storeParamsInQuery]
  );

  const handleRedirectToCreateCampaign = () => {
    navigate(
      CreateCampaignScreenDefinition.navigate({
        campaignId: undefined,
        step: 1,
      })
    );
  };

  const updateCampaignsAfterCampaignFavoriteStatusUpdate = (
    campaigns: CampaignForList[],
    campaignId: number,
    isFavorite: boolean
  ) => {
    const updatedCampaigns = [...campaigns];
    const recordIndex = updatedCampaigns.findIndex(
      (campaign) => campaign.campaignId === campaignId
    );
    if (recordIndex > -1) {
      updatedCampaigns.splice(recordIndex, 1, {
        ...updatedCampaigns[recordIndex],
        isFavorite: isFavorite,
      });
    }

    return updatedCampaigns;
  };

  const { mutate: updateCampaignFavoriteStatusMutation } = useMutation(
    updateCampaignFavoriteStatus,
    {
      onMutate: (data: FavoriteStateChange) => {
        const campaigns = queryClient.getQueryData(
          campaignsQueryKey
        ) as ListCampaignsRO;

        queryClient.setQueryData(
          campaignsQueryKey,
          (oldData?: ListCampaignsRO) => {
            return {
              ...oldData,
              campaigns: updateCampaignsAfterCampaignFavoriteStatusUpdate(
                oldData?.campaigns || [],
                data.campaignId,
                data.isFavorite
              ),
            };
          }
        );

        return {
          previousCampaigns: campaigns,
        };
      },
      onError: (err, data, context) => {
        handleApiError(err, navigate, toast);
        if (context) {
          queryClient.setQueryData(
            campaignsQueryKey,
            context.previousCampaigns
          );
        }
      },
      onSettled: () => {
        /*
         * we have to invalidate queries by "main" prefixes, since changing status on "show favorites" enabled should also cause invalidation
         * of queries for "show favorites" disabled scenario (to ensure that campaigns favorite statuses are up-to-date)
         * */
        void queryClient.invalidateQueries({ queryKey: ["campaignsList"] });
        void queryClient.invalidateQueries({
          queryKey: ["userCampaignsSummary"],
        });
      },
      retry: shouldRetryUnlessStatus(HttpStatus.UNPROCESSABLE_ENTITY),
    }
  );

  const campaignsTableColumns = useCampaignsTableColumns(
    CampaignActionsItems,
    updateCampaignFavoriteStatusMutation
  );

  const hasAccessToCreateCampaign = useHasAccessTo("campaign.create");

  const uniqueKeyAccessor = (i: CampaignForList) => i.campaignId;
  const { selectedRows } = useRowSelection<CampaignForList>(
    rowSelection,
    setRowSelection,
    data?.campaigns,
    uniqueKeyAccessor
  );

  const isAdmin = user?.saasRole === SaasRoleName.Admin;

  return (
    // FIXME: remove this `pb` hack, I suppose it was added to the container so we can fit the
    // campaign context menu. Instead we need to create a menu that fits.
    // In certain situations it makes the scroll bar go crazy.
    <Flex width="100%" flexDirection="column" pb={500}>
      <Flex my={2}>
        <Stats />
      </Flex>
      <Flex
        my={2}
        width="100%"
        alignItems="center"
        justifyContent="space-between"
      >
        <Search
          mb={0}
          currentValue={searchValue}
          onSearch={(value: string) => setSearchValue(value)}
        />
        {hasAccessToCreateCampaign && (
          <Button
            variant="primary-teal"
            size="sm"
            onClick={handleRedirectToCreateCampaign}
          >
            Create Intent Model
          </Button>
        )}
      </Flex>
      <Flex my={2} width="100%">
        <CampaignsTableFilters
          filters={filters}
          onChange={setFilters}
          showAdminFilters={isAdmin}
          showDraftsToggle={hasAccessToDraftCampaigns}
        />
      </Flex>
      <Flex
        mt={2}
        width="100%"
        alignItems="center"
        justifyContent="space-between"
      >
        <BulkActions selectedRows={selectedRows} />
        <TableColumnsToggle
          tableId="campaignsTable"
          columns={campaignsTableColumns}
        />
      </Flex>
      <Flex my={2}>
        <TableComponent
          variant="intentsifyNew"
          searchValue={searchValue}
          defaultSort={[
            {
              id: initialParams.fetchDataParams.order_by ?? "campaignId",
              desc: initialParams.fetchDataParams.order !== SortDirection.ASC,
            },
          ]}
          defaultPageIndex={Math.max(
            0,
            (initialParams.fetchDataParams.page ?? 1) - 1
          )}
          defaultPageSize={initialParams.fetchDataParams.per_page ?? 10}
          columnSettingsId="campaignsTable"
          columns={campaignsTableColumns}
          isFetching={isFetching}
          data={data?.campaigns || []}
          total={data?.total}
          onFetchDataChange={handleOnFetchDataChange}
          resetPaginationDependencies={[searchValue, filters]}
          paginationType={PaginationType.LIMIT_OFFSET}
          borderRadius={0}
          maxWidth="100%"
          headerCellVerticalAlign="top"
          isCellContentCentered
          stretch
          selectable
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          uniqueKeyAccessor={uniqueKeyAccessor}
        />
      </Flex>
    </Flex>
  );
};

export { CampaignsTable };
